/**
 * Preislisten — versionierte Erlös-/Kostensätze je Marke. Genau eine Liste
 * je Marke ist aktiv; Anlegen aus dem Seed (Platzhalter aus den Docs) oder
 * als Kopie einer bestehenden Liste.
 *
 * Backend: GET/POST /api/billing/price-lists, POST …/:id/activate|archive.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PriceListSummary, activatePriceList, archivePriceList, createPriceList, extractApiError, listPriceLists } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { BillingTabs } from '../components/billing/BillingTabs';

const STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Entwurf', cls: 'bg-blue-100 text-blue-800' },
  active: { label: 'Aktiv', cls: 'bg-green-100 text-green-800' },
  archived: { label: 'Archiviert', cls: 'bg-slate-200 text-slate-600' },
};

export function PriceListsPage() {
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const navigate = useNavigate();
  const [rows, setRows] = useState<PriceListSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('sectepe');
  const [copyFrom, setCopyFrom] = useState<string>('');

  function load(): void {
    setLoading(true);
    listPriceLists().then(setRows).catch((e) => setError(extractApiError(e))).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true); setError(null);
    try { await fn(); load(); } catch (e) { setError(extractApiError(e)); } finally { setBusy(false); }
  }

  return (
    <div>
      <div className="mb-2">
        <h2 className="text-xl font-semibold">Preislisten</h2>
        <p className="text-sm text-slate-500">
          Erlös-Sätze (Grundgebühr, Seat, Module, Nutzung) und Selbstkosten-Sätze (IONOS-Stückkosten, Plan-Footprint) je Marke.
          Die aktive Liste wird für Übersicht und Abrechnungslauf verwendet; Abrechnungen behalten die Liste, mit der sie berechnet wurden.
        </p>
      </div>
      <BillingTabs />
      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">{error}</div>}

      {!isReadOnly && (
        <form
          className="bg-white rounded shadow p-4 mb-4 flex flex-wrap items-end gap-3"
          onSubmit={(e) => { e.preventDefault(); void act(async () => { const pl = await createPriceList({ name, brand, fromSeed: !copyFrom, copyFromId: copyFrom || null }); navigate(`/billing/price-lists/${pl.id}`); }); }}
        >
          <div>
            <label className="block text-xs text-slate-500 mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Standard-Preisliste 2026" className="px-2 py-1 border rounded" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Marke</label>
            <input value={brand} onChange={(e) => setBrand(e.target.value.toLowerCase())} className="px-2 py-1 border rounded font-mono w-32" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Vorlage</label>
            <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} className="px-2 py-1 border rounded">
              <option value="">Seed (Platzhalter aus den Docs)</option>
              {rows.map((r) => <option key={r.id} value={r.id}>Kopie von: {r.name} ({r.brand})</option>)}
            </select>
          </div>
          <button type="submit" className="btn-primary" disabled={busy}>Neue Preisliste (Entwurf)</button>
        </form>
      )}

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded text-sm">
          <thead className="bg-slate-100 text-left">
            <tr>
              <th className="p-3">Name</th><th className="p-3">Marke</th><th className="p-3">Status</th><th className="p-3">MwSt</th>
              <th className="p-3">Gültig ab</th><th className="p-3">Positionen</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-medium"><Link to={`/billing/price-lists/${r.id}`} className="text-primary-600 hover:underline">{r.name}</Link></td>
                <td className="p-3 font-mono">{r.brand}</td>
                <td className="p-3"><span className={`badge ${STATUS[r.status]?.cls ?? ''}`}>{STATUS[r.status]?.label ?? r.status}</span></td>
                <td className="p-3 font-mono">{(r.taxRateBp / 100).toLocaleString('de-DE')} %</td>
                <td className="p-3 text-slate-500">{new Date(r.validFrom).toLocaleDateString('de-DE')}</td>
                <td className="p-3">{r.itemCount}</td>
                <td className="p-3 text-right space-x-2">
                  {r.status === 'draft' && <button type="button" className="btn-primary text-xs" disabled={busy || isReadOnly} onClick={() => { if (window.confirm(`"${r.name}" für Marke ${r.brand} aktivieren? Die bisher aktive Liste wird archiviert.`)) void act(() => activatePriceList(r.id)); }}>Aktivieren</button>}
                  {r.status !== 'archived' && <button type="button" className="px-2 py-1 rounded border text-xs" disabled={busy || isReadOnly} onClick={() => { if (window.confirm(`"${r.name}" archivieren?`)) void act(() => archivePriceList(r.id)); }}>Archivieren</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Noch keine Preisliste. Lege eine aus dem Seed an und aktiviere sie, damit die Übersicht Erlöse und Kosten rechnen kann.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}
